import { authGuard } from "@auth0/auth0-vue";
import { differenceInCalendarDays } from "date-fns";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";

import { usePreSignUpData } from "@/composition/api/signup.ts";
import useAuth from "@/composition/shared/useAuth.ts";
import useAuthorization from "@/composition/shared/useAuthorization.ts";
import UseBreakpoints from "@/composition/shared/useBreakpoints.ts";
import { useDeskStore } from "@/stores/desk.ts";
import { useUserStore } from "@/stores/user.ts";
import type { Project } from "@/types/globalTypes";
import { auth0Init } from "@/utils/auth.ts";
import { setSubscriptionIntent } from "@/utils/subscriptionIntent.ts";

function hasPermission(permission: Project | Project[]) {
  const authorization = useAuthorization();
  const checks: Record<Project, () => boolean> = {
    archive: authorization.isPermittedForNewsfeed,
    desk: authorization.isPermittedForDesk,
    cvd: authorization.isPermittedForCvd,
    messenger: authorization.isPermittedForMessenger,
    "ai-research": authorization.isPermittedForAiResearch,
    intelligence: authorization.isPermittedForIntelligence,
  };
  return [permission].flat().some((project) => checks[project]());
}

async function ensurePreSignUpData() {
  const userStore = useUserStore();
  if (
    !userStore.preSignUpFetchedAt ||
    differenceInCalendarDays(new Date(), userStore.preSignUpFetchedAt) >= 1
  ) {
    const { execute: fetchPreSignUpData } = usePreSignUpData();
    await fetchPreSignUpData();
  }
}

function storeSubscriptionIntent(to: RouteLocationNormalized) {
  const subscriptionQuery = to.query.subscription;
  const subscription = Array.isArray(subscriptionQuery)
    ? (subscriptionQuery[0] ?? undefined)
    : (subscriptionQuery ?? undefined);
  if (subscription) {
    setSubscriptionIntent(subscription);
  }
}

function isAuthCallback(to: RouteLocationNormalized) {
  return !!to.query.state && !!(to.query.code || to.query.error);
}

async function completeLogin() {
  try {
    const result = await auth0Init.handleRedirectCallback();
    return result?.appState?.target ?? "/";
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { name: "loginError" };
  }
}

export async function accessGuard(to: RouteLocationNormalized) {
  if (isAuthCallback(to)) {
    return completeLogin();
  }

  storeSubscriptionIntent(to);

  const userStore = useUserStore();
  const access = to.meta.access ?? "app";

  if (access === "public") {
    const { isAuthenticated } = useAuth();
    if (isAuthenticated.value) {
      await userStore.fetchUserPermissions();
      await ensurePreSignUpData();
    }
    return true;
  }

  if (!(await authGuard(to))) {
    return false;
  }

  if (access === "authenticated") {
    return true;
  }

  await ensurePreSignUpData();
  if (userStore.deactivated) {
    return { name: "deactivated" };
  }
  if (userStore.signUpRequired || userStore.activationPending) {
    return { name: "pendingApproval" };
  }

  await userStore.fetchUserPermissions();

  if (to.meta.permission && !hasPermission(to.meta.permission)) {
    return { name: "forbidden" };
  }
  return true;
}

export async function pendingApprovalGuard() {
  const userStore = useUserStore();
  await ensurePreSignUpData();
  return userStore.signUpRequired || userStore.activationPending
    ? true
    : { path: "/" };
}

export async function deactivatedGuard() {
  const userStore = useUserStore();
  await ensurePreSignUpData();
  return userStore.deactivated ? true : { path: "/" };
}

function preferredHomepageRoute(): RouteLocationRaw | undefined {
  const { preferredHomepage, preferredCvdPage } = useUserStore().preferences;
  if (!preferredHomepage || !hasPermission(preferredHomepage)) return undefined;

  const routes: Record<Project, RouteLocationRaw> = {
    archive: { name: "feed" },
    messenger: { name: "messenger" },
    desk: { name: "desk" },
    cvd: {
      name: "cvdPage",
      params: {
        slug: preferredCvdPage ? preferredCvdPage.split(",") : ["top-themen"],
      },
    },
    "ai-research": { name: "ai-research" },
    intelligence: { name: "intelligence" },
  };
  return routes[preferredHomepage];
}

export function homeGuard() {
  const preferredRoute = preferredHomepageRoute();
  if (preferredRoute) return preferredRoute;

  const {
    isPermittedForIntelligence,
    isPermittedForDesk,
    isPermittedForNewsfeed,
  } = useAuthorization();

  if (isPermittedForIntelligence()) return { name: "intelligence" };
  if (isPermittedForDesk()) return { name: "desk" };
  if (isPermittedForNewsfeed()) return { name: "feed" };
  return { name: "forbidden" };
}

export function deskGuard(to: RouteLocationNormalized) {
  const deskStore = useDeskStore();
  if (
    to.query.share &&
    !deskStore.desks.some((desk) => desk.id === to.params.id)
  ) {
    deskStore.joiningDesk = true;
  }
  return true;
}

export function feedDetailGuard(to: RouteLocationNormalized) {
  const { isMobile } = UseBreakpoints();
  if (isMobile.value) {
    return {
      name: "detailPage",
      params: {
        urn: to.params.urn,
      },
    };
  }
  return true;
}
